import { Link } from "react-router-dom";
import { Sparkles, Check, Loader2, AlertTriangle, Bell, Rocket } from "lucide-react";

export default function StepPublish({
  coin,
  promo,
  promoInput,
  setPromoInput,
  promoBusy,
  promoErr,
  applyPromo,
  clearPromo,
  paymentsHealthy,
  paymentsReason,
  waitlistEmail,
  setWaitlistEmail,
  waitlistBusy,
  waitlistDone,
  joinWaitlist,
  publish,
  publishBusy,
  previewOnly,
  back,
  primaryAddress,
}) {
  return (
    <div data-testid="step-3">
      <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter mb-3">
        Publish your coin.
      </h1>
      <p className="text-[#e2e2e2] mb-8">
        $19 unlocks a permanent shareable page at{" "}
        <span className="font-mono text-[#cfff04]">solspur.com/c/{coin.slug}</span> + listing on
        /explore — or grab the <span className="text-[#00ff41]">$29 Launch Bundle</span> with free
        on-chain minting + a 24h boost. Free preview always available below.
      </p>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 md:col-span-4 border border-[#3a3a3a] bg-[#121212] p-6">
          <div className="font-mono text-xs uppercase tracking-widest text-[#e2e2e2] mb-2">
            Free Preview
          </div>
          <div className="font-display text-2xl font-bold mb-2">$0</div>
          <ul className="text-sm text-[#e2e2e2] space-y-1 mb-5">
            <li className="flex gap-2"><Check size={14} className="text-[#00ff41]" /> Shareable preview link</li>
            <li className="flex gap-2"><Check size={14} className="text-[#00ff41]" /> AI logo + whitepaper</li>
            <li className="flex gap-2"><Check size={14} className="text-[#d0d0d0]" /> Listed on /explore</li>
            <li className="flex gap-2"><Check size={14} className="text-[#d0d0d0]" /> Trade simulator</li>
          </ul>
          <button
            onClick={previewOnly}
            data-testid="publish-skip"
            className="w-full border border-[#3a3a3a] hover:border-[#cfff04] hover:text-[#cfff04] text-[#e2e2e2] py-3 font-mono text-xs uppercase tracking-widest"
          >
            Just preview →
          </button>
        </div>

        <div className="col-span-12 md:col-span-4 border border-[#cfff04] bg-[#121212] p-6 relative">
          <span className="absolute -top-3 left-6 bg-[#cfff04] text-[#050505] font-mono text-[11px] uppercase tracking-widest px-2 py-0.5">
            Recommended
          </span>
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#cfff04] mb-2">
            Publish
          </div>
          <div className="font-display text-2xl font-bold mb-2 flex items-baseline gap-2">
            {promo ? (
              <>
                <span>${promo.final_amount}</span>
                <span className="text-sm font-mono text-[#d0d0d0] line-through">${promo.list_amount}</span>
                <span className="text-[11px] font-mono uppercase tracking-widest text-[#cfff04] bg-[#cfff04]/10 border border-[#cfff04]/30 px-1.5 py-0.5">
                  −{promo.percent_off}% {promo.code}
                </span>
              </>
            ) : (
              <span>$19</span>
            )}
          </div>
          <ul className="text-sm text-[#e2e2e2] space-y-1 mb-5">
            <li className="flex gap-2"><Check size={14} className="text-[#00ff41]" /> Listed on /explore</li>
            <li className="flex gap-2"><Check size={14} className="text-[#00ff41]" /> Trade simulator unlocked</li>
            <li className="flex gap-2"><Check size={14} className="text-[#00ff41]" /> Edit anytime</li>
            <li className="flex gap-2"><Check size={14} className="text-[#00ff41]" /> Permanent shareable URL</li>
          </ul>

          {/* Promo code */}
          {promo ? (
            <div className="mb-4 flex items-center justify-between border border-[#cfff04]/30 bg-[#cfff04]/5 px-3 py-2">
              <span className="font-mono text-[11px] uppercase tracking-widest text-[#cfff04]">
                {promo.code} applied
              </span>
              <button
                type="button"
                onClick={clearPromo}
                data-testid="promo-clear"
                className="font-mono text-[11px] uppercase tracking-widest text-[#e2e2e2] hover:text-[#cfff04]"
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="mb-4">
              <div className="flex gap-2 min-w-0">
                <input
                  type="text"
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                  placeholder="Promo code"
                  maxLength={24}
                  data-testid="promo-input"
                  className="flex-1 min-w-0 w-0 bg-[#050505] border border-[#3d3d3d] focus:border-[#cfff04] focus:outline-none placeholder:text-[#b8b8b8] px-3 py-2 font-mono text-xs uppercase tracking-widest placeholder:text-[#d0d0d0]"
                />
                <button
                  type="button"
                  onClick={applyPromo}
                  disabled={promoBusy || !promoInput.trim()}
                  data-testid="promo-apply"
                  className="flex-shrink-0 border border-[#3a3a3a] hover:border-[#cfff04] hover:text-[#cfff04] text-[#e2e2e2] px-3 font-mono text-[11px] uppercase tracking-widest disabled:opacity-40"
                >
                  {promoBusy ? "…" : "Apply"}
                </button>
              </div>
              {promoErr && (
                <p className="mt-1 font-mono text-[11px] text-red-400 uppercase tracking-widest" data-testid="promo-error">
                  {promoErr}
                </p>
              )}
            </div>
          )}

          {/* Pre-flight payments-offline banner — only shown if Stripe is verified down */}
          {paymentsHealthy === false && (
            <div
              className="mb-4 border border-amber-400/40 bg-amber-400/5 p-4"
              data-testid="payments-offline-banner"
            >
              <div className="flex items-start gap-2 mb-3">
                <AlertTriangle size={16} className="text-amber-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-display font-bold text-sm text-amber-400 mb-1">
                    Payments are temporarily offline
                  </div>
                  <p className="text-xs text-[#e2e2e2] leading-relaxed">
                    Your draft <span className="text-white font-mono">{coin.slug}</span> is saved.
                    Drop your email and we&apos;ll ping you the moment payments are back —
                    usually within minutes.
                  </p>
                </div>
              </div>
              {waitlistDone ? (
                <div className="flex items-center gap-2 border border-[#00ff41]/40 bg-[#00ff41]/5 px-3 py-2">
                  <Check size={14} className="text-[#00ff41]" />
                  <span className="font-mono text-[11px] uppercase tracking-widest text-[#00ff41]">
                    On the list — check your inbox
                  </span>
                </div>
              ) : (
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Bell size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#d0d0d0]" />
                    <input
                      type="email"
                      value={waitlistEmail}
                      onChange={(e) => setWaitlistEmail(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && waitlistEmail.trim() && !waitlistBusy) joinWaitlist();
                      }}
                      placeholder="you@email.com"
                      data-testid="waitlist-input"
                      className="w-full bg-[#0c0c0c] border border-[#3a3a3a] focus:border-amber-400 focus:outline-none pl-8 pr-3 py-2 font-mono text-xs placeholder:text-[#d0d0d0]"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={joinWaitlist}
                    disabled={waitlistBusy || !waitlistEmail.trim()}
                    data-testid="waitlist-submit"
                    className="border border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-[#050505] px-4 font-mono text-[11px] uppercase tracking-widest disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-amber-400 transition-colors"
                  >
                    {waitlistBusy ? "…" : "Notify"}
                  </button>
                </div>
              )}
              <p className="mt-3 font-mono text-xs text-[#d0d0d0] uppercase tracking-wider">
                // status: {paymentsReason || "checking"} · we don&apos;t spam, ever
              </p>
            </div>
          )}

          <button
            onClick={() => publish("PUBLISH")}
            disabled={!!publishBusy || paymentsHealthy === false}
            data-testid="publish-pay"
            className="w-full btn-brutal bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-sm py-3 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {publishBusy === "PUBLISH" ? (
              <Loader2 size={16} className="animate-spin" />
            ) : paymentsHealthy === false ? (
              <AlertTriangle size={16} />
            ) : (
              <Sparkles size={16} />
            )}
            {paymentsHealthy === false
              ? "Payments offline"
              : `Publish for $${promo ? promo.final_amount : "19"}`}
          </button>
          <p className="mt-3 font-mono text-xs text-[#d0d0d0] uppercase tracking-wider leading-relaxed">
            By publishing you agree to our{" "}
            <Link to="/terms" className="text-[#e2e2e2] hover:text-[#cfff04] underline" data-testid="publish-tos-link">
              Terms
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="text-[#e2e2e2] hover:text-[#cfff04] underline" data-testid="publish-privacy-link">
              Privacy Policy
            </Link>
            .
          </p>
        </div>

        <div className="col-span-12 md:col-span-4 border border-[#00ff41] bg-[#121212] p-6 relative" data-testid="bundle-card">
          <span className="absolute -top-3 left-6 bg-[#00ff41] text-[#050505] font-mono text-[11px] uppercase tracking-widest px-2 py-0.5">
            Best value
          </span>
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#00ff41] mb-2">
            Launch Bundle
          </div>
          <div className="font-display text-2xl font-bold mb-2">$29</div>
          <ul className="text-sm text-[#e2e2e2] space-y-1 mb-5">
            <li className="flex gap-2"><Check size={14} className="text-[#00ff41]" /> Everything in Publish</li>
            <li className="flex gap-2"><Check size={14} className="text-[#00ff41]" /> On-chain mint — SolSpur fee waived</li>
            <li className="flex gap-2"><Check size={14} className="text-[#00ff41]" /> 24h Featured boost on /explore</li>
            <li className="flex gap-2"><Check size={14} className="text-[#00ff41]" /> Real SPL token via one Phantom click</li>
          </ul>
          <button
            onClick={() => publish("PUBLISH_MINT")}
            disabled={!!publishBusy || paymentsHealthy === false}
            data-testid="publish-bundle-pay"
            className="w-full btn-brutal bg-[#00ff41] text-[#050505] font-bold uppercase tracking-wider text-sm py-3 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {publishBusy === "PUBLISH_MINT" ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Rocket size={16} />
            )}
            {paymentsHealthy === false ? "Payments offline" : "Launch Bundle $29"}
          </button>
          <p className="mt-3 font-mono text-xs text-[#d0d0d0] uppercase tracking-wider leading-relaxed">
            Mint on devnet free anytime. Mainnet mint normally carries a 0.02 SOL platform fee — waived here.
          </p>
        </div>
      </div>

      {primaryAddress && (
        <p className="mt-6 font-mono text-xs text-[#d0d0d0] uppercase tracking-widest text-center">
          // we&apos;ll auto-link {primaryAddress.slice(0, 6)}…{primaryAddress.slice(-4)} as the creator
        </p>
      )}

      <div className="mt-8">
        <button onClick={back} className="font-mono text-xs uppercase tracking-widest text-[#e2e2e2] hover:text-white" data-testid="step-back">
          ← Back
        </button>
      </div>
    </div>
  );
}
